import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import SitemarkIcon from "../components/landing/SitemarkIcon";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";
import toast from "react-hot-toast";

const ForgotPasswordContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.forgotPassword(email);

      if (response.success) {
        setEmailSent(true);
        toast.success("Password reset instructions sent to your email");
      } else {
        setError(response.message || "Failed to send reset email");
      }
    } catch (error: any) {
      console.error("Forgot password error:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <>
        <CssBaseline enableColorScheme />
        <ForgotPasswordContainer direction="column" justifyContent="center">
          <Box
            sx={{
              position: "absolute",
              top: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
            }}
          >
            <ColorModeIconDropdown />
          </Box>
          <Stack
            sx={{
              justifyContent: "center",
              height: "100dvh",
              p: 2,
            }}
          >
            <Card
              variant="outlined"
              sx={{ maxWidth: 450, mx: "auto", width: "100%" }}
            >
              <CardContent sx={{ textAlign: "center", p: 4 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "success.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <EmailIcon sx={{ fontSize: 32, color: "success.main" }} />
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Check your email
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  We've sent password reset instructions to{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {email}
                  </Typography>
                </Typography>

                <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                  <AlertTitle>Didn't receive the email?</AlertTitle>
                  <Box
                    component="ul"
                    sx={{ mt: 1, pl: 2, fontSize: "0.875rem" }}
                  >
                    <li>Check your spam folder</li>
                    <li>Make sure you entered the correct email</li>
                    <li>Wait a few minutes and check again</li>
                  </Box>
                </Alert>

                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setEmailSent(false)}
                  >
                    Try another email
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/login")}
                  >
                    Back to login
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </ForgotPasswordContainer>
      </>
    );
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <ForgotPasswordContainer direction="column" justifyContent="center">
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
          }}
        >
          <ColorModeIconDropdown />
        </Box>
        <Stack
          sx={{
            justifyContent: "center",
            height: "100dvh",
            p: 2,
          }}
        >
          <Card
            variant="outlined"
            sx={{ maxWidth: 450, mx: "auto", width: "100%" }}
          >
            <CardContent sx={{ p: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/login")}
                sx={{ mb: 3 }}
              >
                Back to login
              </Button>

              <Box sx={{ mb: 3 }}>
                <SitemarkIcon />
              </Box>

              <Typography variant="h4" sx={{ mb: 1 }}>
                Forgot your password?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No worries! Enter your email address and we'll send you
                instructions to reset your password.
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <TextField
                    error={Boolean(error)}
                    helperText={error}
                    id="email"
                    type="email"
                    name="email"
                    placeholder="your.email@company.com"
                    autoComplete="email"
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                    label="Email address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {loading ? "Sending..." : "Send reset instructions"}
                </Button>

                <Typography sx={{ textAlign: "center", fontSize: "0.875rem" }}>
                  Remember your password?{" "}
                  <Link
                    onClick={() => navigate("/login")}
                    sx={{ cursor: "pointer", fontWeight: 600 }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </ForgotPasswordContainer>
    </>
  );
};

export default ForgotPassword;
